"use client";

// Décorations du path (parité EXACTE avec l'app mobile, PathDecoration.tsx) :
// des illustrations ou animations Lottie OPTIONNELLES posées dans les CREUX
// du zigzag, du côté opposé au virage des nœuds — purement décoratives
// (aucune interaction, derrière les nœuds). Unité verrouillée (ni faite ni en
// cours) : décor en NOIR ET BLANC (filtre CSS) et figé à mi-animation, comme
// Duolingo.
//
// PILOTÉES PAR L'ADMIN believemy (bouton « Décor » de chaque partie) : l'unité
// du curriculum porte jusqu'à DEUX jeux de champs — creux 1 (decorationImage /
// decorationLottie…) et creux 2 (mêmes champs suffixés "2", parité Duolingo
// sur les unités longues). Rien en base = rien affiché.
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import PublicLottieAnimation from "@/components/PublicLottieAnimation/PublicLottieAnimation";

const LOTTIE_MODES = ["loop", "loop-reverse", "once", "once-reverse", "bounce"];

/** Centre VISUEL d'un nœud régulier sous la frontière où le décor est inséré :
 * milieu de la PASTILLE ENTIÈRE (face elliptique 55px + lèvre 3D 8px) / 2 ≈ 32,
 * et non le centre de la seule face (~28) — l'œil lit la boule complète, lèvre
 * comprise, donc caler sur la face fait paraître le décor trop haut. */
const NODE_FACE_CENTER = 32;

/** Taille du décor. Tous les creux se valent désormais : la forme du chemin ne
 * produit plus que des virages ENTIERS (cf. lib/reps/pathShape.js), il n'y a
 * donc plus de creux « à mi-course » qui justifiait un décor rapetissé. */
const DECORATION_SIZE = 190;

// Décor d'un creux : uniquement si l'admin a posé les champs du slot. Le décor
// est ensuite CENTRÉ dans la moitié vide de la colonne (cf. rendu) : bien
// centré sous la bannière d'unité et à distance des nœuds, quelle que soit la
// largeur d'écran.
function buildDecoration(unit, suffix, slot) {
    if (!slot) return null;
    const lottieName = unit[`decorationLottie${suffix}`] || null;
    const imageUrl = unit[`decorationImage${suffix}`] || null;
    if (!lottieName && !imageUrl) return null;

    const speed = unit[`decorationLottieSpeed${suffix}`] ?? 1;
    const play = unit[`decorationLottiePlay${suffix}`];
    return {
        lottieName,
        lottieSpeed: speed >= 0.25 && speed <= 3 ? speed : 1,
        lottieMode: play && LOTTIE_MODES.includes(play) ? play : "loop",
        imageUrl,
        afterNode: slot.afterNode,
        shift: slot.shift,
        side: slot.side,
        size: DECORATION_SIZE,
    };
}

// Décors d'une unité (0 à 2), posés sur les emplacements que la forme du
// chemin a produits : un par virage, dans l'ordre. Plus rien à deviner ici —
// une unité assez longue pour dessiner deux virages porte deux décors, une
// unité plus courte n'en porte qu'un et le second reste sans effet (jamais
// forcé). Même logique que decorationsForUnit mobile.
export function decorationsForUnit(unit, slots) {
    const decorations = [];
    const first = buildDecoration(unit, "", slots[0]);
    if (first) decorations.push(first);
    const second = buildDecoration(unit, "2", slots[1]);
    if (second) decorations.push(second);
    return decorations;
}

// Lecteur du Lottie de décoration : les modes admin (loop, inversés, bounce,
// once) sont matérialisés par PublicLottieAnimation (le même lecteur que les
// leçons). Verrouillé : autoplay coupé et arrêt sur la frame du MILIEU (la
// première peut être vide quand l'animation démarre par un fondu d'entrée).
function DecorLottie({ name, speed, mode, locked }) {
    const [anim, setAnim] = useState(null);
    const freezeRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        fetch(`/api/reps/lottie/${encodeURIComponent(name)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
                if (!cancelled && json && typeof json === "object") {
                    setAnim(json);
                }
            })
            .catch(() => {
                // décor = bonus : jamais d'erreur visible
            });
        return () => {
            cancelled = true;
        };
    }, [name]);

    if (!anim) return null;

    if (locked) {
        return (
            <PublicLottieAnimation
                key="frozen"
                animation={anim}
                stopAutoPlay
                ref={freezeRef}
                onDomLoaded={() => {
                    // Nombre de frames lu dans le JSON (op - ip, comme
                    // lottie-web) : au moment où l'animation est prête, la
                    // poignée de lottie-react n'a pas toujours reçu son
                    // animationItem. seek ne relance pas la lecture.
                    const inst = freezeRef.current;
                    const total = Math.floor(anim.op - anim.ip) || 2;
                    if (inst && typeof inst.seek === "function") {
                        inst.seek(Math.floor(total / 2));
                    }
                }}
            />
        );
    }
    return (
        <PublicLottieAnimation
            key="playing"
            animation={anim}
            speed={speed}
            mode={mode}
        />
    );
}

// Rendu : wrapper de hauteur nulle inséré juste avant le nœud du virage
// (n'affecte jamais le flux du chemin), contenu absolu centré VERTICALEMENT
// sur le sommet du virage et HORIZONTALEMENT au milieu de la moitié vide de la
// colonne (25 % côté gauche, 75 % côté droit) : le décor tombe bien centré sous
// la bannière d'unité, à distance des nœuds, quelle que soit la largeur.
const LOCKED_FILTER = "grayscale(1) contrast(0.6) brightness(1.45)";

export default function RepsPathDecoration({ decoration, locked }) {
    const { size, side, shift } = decoration;
    return (
        <div className="pointer-events-none relative h-0 w-full" aria-hidden="true">
            <div
                style={{
                    // Verrouillé : noir et blanc ÉCLAIRCI (gris = 0,87 × lum + 74,
                    // même courbe que le proxy decoration-gray du mobile). Un
                    // simple grayscale rendait BeBot presque noir, jugé froid.
                    filter: locked ? LOCKED_FILTER : undefined,
                    position: "absolute",
                    // shift : demi-pas de descente quand le sommet du virage
                    // tombe entre deux nœuds (virage de longueur impaire).
                    top: NODE_FACE_CENTER + (shift || 0) - size / 2,
                    left: side === "left" ? "25%" : "75%",
                    transform: "translateX(-50%)",
                    width: size,
                    height: size,
                }}
            >
                {decoration.lottieName ? (
                    <DecorLottie
                        name={decoration.lottieName}
                        speed={decoration.lottieSpeed}
                        mode={decoration.lottieMode}
                        locked={locked}
                    />
                ) : decoration.imageUrl ? (
                    <Image
                        src={decoration.imageUrl}
                        alt=""
                        width={size}
                        height={size}
                        className="h-full w-full object-contain"
                    />
                ) : null}
            </div>
        </div>
    );
}
